// useCustomers Hook - Manages customer data loading and selection

import { useState, useEffect, useCallback } from 'react';
import { Customer } from '../../../api/mobiApi/domain/domain.types';
import { fbsApiService } from '../../../api/mobiApi/fbs-api.service';

interface UseCustomersReturn {
  customers: Customer[];
  loading: boolean;
  geocodingProgress: number;
  error: string | null;
  loadCustomers: (startDate?: string, endDate?: string) => Promise<void>;
  toggleCustomerSelection: (customerId: string | number) => void;
  selectAll: () => void;
  deselectAll: () => void;
  getSelectedCustomers: () => Customer[];
  getTotalStats: () => {
    totalCustomers: number;
    selectedCustomers: number;
    totalDemand: number;
    selectedDemand: number;
  };
  clearCache: () => void;
  refreshCustomers: () => void;
}

const STORAGE_KEY = 'mobiroute_customers';

export const useCustomers = (): UseCustomersReturn => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [geocodingProgress, setGeocodingProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Load customers from localStorage on mount
  useEffect(() => {
    const storedCustomers = localStorage.getItem(STORAGE_KEY);
    if (storedCustomers) {
      try {
        const parsed = JSON.parse(storedCustomers);
        setCustomers(Array.isArray(parsed) ? parsed : []);
      } catch (err) {
        console.error('Failed to parse stored customers:', err);
        setCustomers([]);
      }
    }
  }, []);

  // Save customers to localStorage whenever they change
  useEffect(() => {
    if (customers.length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(customers));
    }
  }, [customers]);

  // Geocode address using Nominatim API
  const geocodeAddress = async (address: string): Promise<{ lat: number; lon: number } | null> => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(address)}&format=json&limit=1`,
        {
          headers: {
            'User-Agent': 'MobiRoute-FBS-App/1.0'
          }
        }
      );

      const data = await response.json();

      if (data && data.length > 0) {
        return {
          lat: parseFloat(data[0].lat),
          lon: parseFloat(data[0].lon)
        };
      }

      return null;
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    }
  };

  const loadCustomers = useCallback(async (startDate?: string, endDate?: string) => {
    setLoading(true);
    setError(null);
    setGeocodingProgress(0);

    try {
      console.log(`📡 Loading customers from FBS API (${startDate} to ${endDate})`);

      // Call FBS API Service to get customers
      const fbsCustomers = await fbsApiService.fetchCustomers(startDate, endDate);

      console.log(`✅ Loaded ${fbsCustomers.length} customers from FBS API Service`);

      // Find customers without coordinates
        const customersWithoutCoords = fbsCustomers.filter(c => {
          const hasValidLat = c.lat && !isNaN(c.lat) && c.lat !== 0;
          const hasValidLon = c.lon && !isNaN(c.lon) && c.lon !== 0;
          return !hasValidLat || !hasValidLon;
        });

        console.log(`🔍 Customers without coords: ${customersWithoutCoords.length}`);

        // Debug: Show first few customers without coords
        if (customersWithoutCoords.length > 0) {
          console.log('🔍 Sample customers needing geocoding:', customersWithoutCoords.slice(0, 3).map(c => ({
            id: c.id,
            name: c.name,
            address: c.address,
            lat: c.lat,
            lon: c.lon
          })));
        }

        if (customersWithoutCoords.length > 0) {
          console.log(`🌍 Geocoding ${customersWithoutCoords.length} customers without coordinates...`);

          // Parallel geocoding with batch size of 10
          const BATCH_SIZE = 4;
          const DELAY_BETWEEN_BATCHES = 1000; // 1 second delay between batches

          for (let batchStart = 0; batchStart < customersWithoutCoords.length; batchStart += BATCH_SIZE) {
            const batchEnd = Math.min(batchStart + BATCH_SIZE, customersWithoutCoords.length);
            const batch = customersWithoutCoords.slice(batchStart, batchEnd);

            console.log(`🌍 Processing batch ${Math.floor(batchStart / BATCH_SIZE) + 1}: ${batchStart + 1}-${batchEnd} of ${customersWithoutCoords.length}`);

            // Process all customers in this batch in parallel
            const geocodePromises = batch.map(async (customer) => {
              if (customer.address) {
                console.log(`🌍 Geocoding: ${customer.name} - ${customer.address}`);

                const coords = await geocodeAddress(customer.address);

                if (coords) {
                  // Update customer coordinates
                  const customerIndex = fbsCustomers.findIndex(c => c.id === customer.id);
                  if (customerIndex !== -1) {
                    fbsCustomers[customerIndex].lat = coords.lat;
                    fbsCustomers[customerIndex].lon = coords.lon;
                    console.log(`✅ Geocoded: ${customer.name} -> ${coords.lat}, ${coords.lon}`);
                  }
                  return true;
                } else {
                  console.log(`⚠️ Could not geocode: ${customer.name}`);
                  return false;
                }
              }
              return false;
            });

            // Wait for all requests in this batch to complete
            await Promise.all(geocodePromises);

            // Update progress
            const progress = Math.round((batchEnd / customersWithoutCoords.length) * 100);
            setGeocodingProgress(progress);

            // Wait before next batch (rate limiting)
            if (batchEnd < customersWithoutCoords.length) {
              console.log(`⏳ Waiting ${DELAY_BETWEEN_BATCHES}ms before next batch...`);
              await new Promise(resolve => setTimeout(resolve, DELAY_BETWEEN_BATCHES));
            }
          }

          console.log(`✅ Geocoding complete!`);
        }

        // Filter only customers with valid coordinates
        const validCustomers = fbsCustomers.filter(c => {
          const lat = c?.lat;
          const lon = c?.lon;
          return Number.isFinite(lat) && Number.isFinite(lon) && lat !== 0 && lon !== 0;
        });

        const invalidCount = fbsCustomers.length - validCustomers.length;

        console.log(`✅ Valid customers: ${validCustomers.length}`);
        if (invalidCount > 0) {
          console.log(`⚠️ Skipped ${invalidCount} customers without valid coordinates`);
        }

        setCustomers(validCustomers);

      // Save ONLY valid customers to localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify(validCustomers));
      console.log(`💾 Saved ${validCustomers.length} valid customers to localStorage`);

      setGeocodingProgress(100);
    } catch (err: any) {
      setError(err.message || 'Failed to load customers from FBS API');
      console.error('❌ Error loading customers:', err);

      // Fallback to localStorage if API fails
      const storedCustomers = localStorage.getItem(STORAGE_KEY);
      if (storedCustomers) {
        const parsed = JSON.parse(storedCustomers);
        setCustomers(Array.isArray(parsed) ? parsed : []);
        console.log('⚠️ Loaded customers from localStorage cache');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const toggleCustomerSelection = useCallback((customerId: string | number) => {
    setCustomers(prevCustomers =>
      prevCustomers.map(customer =>
        customer.id === customerId
          ? { ...customer, selected: !customer.selected }
          : customer
      )
    );
  }, []);

  const selectAll = useCallback(() => {
    setCustomers(prevCustomers =>
      prevCustomers.map(customer => ({ ...customer, selected: true }))
    );
  }, []);

  const deselectAll = useCallback(() => {
    setCustomers(prevCustomers =>
      prevCustomers.map(customer => ({ ...customer, selected: false }))
    );
  }, []);

  const getSelectedCustomers = useCallback(() => {
    return customers.filter(customer => customer.selected);
  }, [customers]);

  const getTotalStats = useCallback(() => {
    const selectedCustomers = customers.filter(c => c.selected);
    return {
      totalCustomers: customers.length,
      selectedCustomers: selectedCustomers.length,
      totalDemand: customers.reduce((sum, c) => sum + (c.demand || 0), 0),
      selectedDemand: selectedCustomers.reduce((sum, c) => sum + (c.demand || 0), 0),
    };
  }, [customers]);

  const clearCache = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setCustomers([]);
  }, []);

  const refreshCustomers = useCallback(() => {
    const storedCustomers = localStorage.getItem(STORAGE_KEY);
    if (storedCustomers) {
      try {
        const parsed = JSON.parse(storedCustomers);
        setCustomers(Array.isArray(parsed) ? parsed : []);
      } catch (err) {
        console.error('Failed to parse stored customers:', err);
        setCustomers([]);
      }
    }
  }, []);

  return {
    customers,
    loading,
    geocodingProgress,
    error,
    loadCustomers,
    toggleCustomerSelection,
    selectAll,
    deselectAll,
    getSelectedCustomers,
    getTotalStats,
    clearCache,
    refreshCustomers,
  };
};
